import { create } from "zustand";
import type { LayerType } from "../types";

interface AppState {
  /** Selected hindcast name (null = none selected) */
  selectedHindcast: string | null;
  /** Validated lon/lat from map click or manual input */
  lon: number | null;
  lat: number | null;
  /** Search radius in km */
  radius: number;
  /** Which gridded layer to display */
  layer: LayerType;
  /** Whether grid domain outlines are visible */
  showGrids: boolean;
  /** Whether particle animations are visible */
  showParticles: boolean;
  /** Whether the validation modal is open */
  validationOpen: boolean;
  /** Whether the hindcast info modal is open */
  hindcastInfoOpen: boolean;
  /** Whether the satellite info modal is open */
  satelliteInfoOpen: boolean;

  // Actions
  setHindcast: (name: string | null) => void;
  setLocation: (lon: number, lat: number) => void;
  setRadius: (r: number) => void;
  setLayer: (l: LayerType) => void;
  /** Restore all validation params atomically (used by recent-validations history). */
  restoreValidation: (hindcast: string, lon: number, lat: number, radius: number, layer: LayerType) => void;
  toggleGrids: () => void;
  toggleParticles: () => void;
  openValidation: () => void;
  closeValidation: () => void;
  openHindcastInfo: () => void;
  closeHindcastInfo: () => void;
  openSatelliteInfo: () => void;
  closeSatelliteInfo: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  selectedHindcast: null,
  lon: null,
  lat: null,
  radius: 50,
  layer: "wave",
  showGrids: true,
  showParticles: true,
  validationOpen: false,
  hindcastInfoOpen: false,
  satelliteInfoOpen: false,

  setHindcast: (name) =>
    set({ selectedHindcast: name, lon: null, lat: null, validationOpen: false }),

  setLocation: (lon, lat) => set({ lon, lat }),

  setRadius: (radius) => set({ radius }),

  setLayer: (layer) => set({ layer }),

  restoreValidation: (hindcast, lon, lat, radius, layer) =>
    set({ selectedHindcast: hindcast, lon, lat, radius, layer }),

  toggleGrids: () => set((s) => ({ showGrids: !s.showGrids })),
  toggleParticles: () => set((s) => ({ showParticles: !s.showParticles })),

  openValidation: () => set({ validationOpen: true }),
  closeValidation: () => set({ validationOpen: false }),

  openHindcastInfo: () => set({ hindcastInfoOpen: true }),
  closeHindcastInfo: () => set({ hindcastInfoOpen: false }),

  openSatelliteInfo: () => set({ satelliteInfoOpen: true }),
  closeSatelliteInfo: () => set({ satelliteInfoOpen: false }),
}));
